import React, { useState, useEffect } from "react";
import { useDocumentContext } from "../context/DocumentContext";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ArrowLeft,
  Save,
  FileText,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { getApiBase } from "../config";
import { getToken } from "../auth";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_BASE_URL = getApiBase();

interface SSNSegment {
  id: string;
  ssn: string;
  startPage: number;
  endPage: number;
  pageCount: number;
  purpose: string;
}

export default function PromissoryNotePage() {
  const navigate = useNavigate();
  const { file, analysisData } = useDocumentContext();

  const [segments, setSegments] = useState<SSNSegment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [scale, setScale] = useState<number>(1.0);

  // Initialize segments from analysis data (SSN-based document_analysis)
  useEffect(() => {
    if (!file) {
      navigate("/");
      return;
    }

    if (analysisData?.document_analysis && segments.length === 0) {
      const ssnSegments: SSNSegment[] = analysisData.document_analysis.map(
        (doc: any, index: number) => ({
          id: `ssn-${index}`,
          ssn: doc.document_type || "UNKNOWN",
          startPage: Number(doc.start_page),
          endPage: Number(doc.end_page),
          pageCount: Number(doc.end_page) - Number(doc.start_page) + 1,
          purpose: doc.primary_purpose || "",
        })
      );

      if (ssnSegments.length > 0) {
        setSegments(ssnSegments);
        setSelectedSegmentId(ssnSegments[0].id);
        setPreviewPage(ssnSegments[0].startPage);
      }
    }
  }, [analysisData, file, navigate]);

  // When segment changes, always jump to the segment's first page
  useEffect(() => {
    const seg = segments.find((s) => s.id === selectedSegmentId);
    if (seg) {
      setPreviewPage(seg.startPage);
    }
  }, [selectedSegmentId]);

  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  const handleSave = async () => {
    if (!file || !analysisData) return;
    setIsSaving(true);

    try {
      const token = getToken();
      const splitRules = segments.map((seg) => ({
        page_start: seg.startPage,
        page_end: seg.endPage,
        target_doc_type: seg.ssn,
        final_filename: `PNOTE_${seg.ssn}`,
      }));

      const payload = {
        document_name: file.name,
        fingerprint_hash: analysisData?.meta?.fingerprint_hash || "",
        fingerprint_version: analysisData?.meta?.fingerprint_version || 2,
        jurisdiction_code: analysisData?.jurisdiction_and_context?.jurisdiction_state || "UNKNOWN",
        payload: {
          splits: splitRules,
        },
        meta: {
          total_pages: numPages,
          process_mode: "promissory_note",
        },
      };

      const res = await fetch(`${API_BASE_URL}/process-job/split-rules`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Save failed: ${res.status}`);

      alert("Promissory note template saved successfully!");
      navigate("/dashboard");
    } catch (err: any) {
      alert(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!file || !analysisData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col items-center justify-center p-10 text-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Context Found</h2>
        <p className="text-gray-500 dark:text-slate-400 mb-6">Please upload a file first.</p>
        <button onClick={() => navigate("/")} className="bg-blue-600 text-white px-4 py-2 rounded-lg">
          Go to Upload
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* --- LEFT PANEL: SSN Segments --- */}
      <div className="w-80 flex-shrink-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            Promissory Note
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {segments.length} SSN group{segments.length !== 1 ? "s" : ""} detected
          </p>
        </div>

        {/* Segment List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {segments.map((seg, idx) => (
            <button
              key={seg.id}
              onClick={() => setSelectedSegmentId(seg.id)}
              className={`w-full text-left px-3 py-3 rounded-lg transition-all text-sm ${
                selectedSegmentId === seg.id
                  ? "bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-slate-900 dark:text-white">
                  {seg.ssn}
                </span>
                <span className="text-xs text-slate-400">
                  {seg.pageCount}p
                </span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Pages {seg.startPage}–{seg.endPage}
              </div>
            </button>
          ))}
        </div>

        {/* Save Button */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={handleSave}
            disabled={isSaving || segments.length === 0}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-white transition-all ${
              isSaving || segments.length === 0
                ? "bg-slate-300 dark:bg-slate-700 cursor-not-allowed"
                : "bg-amber-600 hover:bg-amber-500 shadow-lg shadow-amber-500/25"
            }`}
          >
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {isSaving ? "Saving..." : "Confirm & Save Template"}
          </button>
        </div>
      </div>

      {/* --- RIGHT PANEL: PDF Preview --- */}
      <div className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex flex-col">
        {/* Preview Header — scoped to selected segment's page range */}
        <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => selectedSegment && setPreviewPage(Math.max(selectedSegment.startPage, previewPage - 1))}
              disabled={!selectedSegment || previewPage <= (selectedSegment?.startPage ?? 1)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-mono text-slate-600 dark:text-slate-300">
              {selectedSegment
                ? `Page ${previewPage} / ${selectedSegment.endPage} (${previewPage - selectedSegment.startPage + 1} of ${selectedSegment.pageCount})`
                : `Page ${previewPage} / ${numPages}`
              }
            </span>
            <button
              onClick={() => selectedSegment && setPreviewPage(Math.min(selectedSegment.endPage, previewPage + 1))}
              disabled={!selectedSegment || previewPage >= (selectedSegment?.endPage ?? numPages)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {selectedSegment && (
            <div className="text-sm text-amber-600 dark:text-amber-400 font-semibold">
              SSN: {selectedSegment.ssn} ({selectedSegment.pageCount} pages)
            </div>
          )}

          <div className="flex items-center gap-1">
            <button onClick={() => setScale(Math.max(0.5, scale - 0.1))} className="px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-800">-</button>
            <span className="text-xs font-mono w-12 text-center">{(scale * 100).toFixed(0)}%</span>
            <button onClick={() => setScale(Math.min(2.0, scale + 0.1))} className="px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-800">+</button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-auto flex justify-center p-4 bg-slate-50 dark:bg-slate-950">
          <Document
            file={file}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<Loader2 className="w-8 h-8 animate-spin text-slate-400" />}
          >
            <Page
              pageNumber={previewPage}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>
      </div>
    </div>
  );
}
